import React, {createRef, useEffect, useRef, useState} from "react";
import {Fade, Modal} from "@mui/material";
import {ArrowBackIos, ArrowForwardIos, Close, ZoomIn, ZoomOut} from "@mui/icons-material";
import IconButton from "../../base/button/icon_button";
import PrismaZoom from 'react-prismazoom';
import Utils, {formatMoney} from "../../../../core/services/utils/utils";

const ImagesPreviewModal = ({open, setOpen, images}) => {
    const [refs, setRefs] = useState([]);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrev, setHasPrev] = useState(false);
    /**@type {React.MutableRefObject<HTMLDivElement>}*/
    const containerRef = useRef();

    /**
     * Listens to open and images and with each change:
     * - if open, then sets the refs of this modal.
     */
    useEffect(() => {
        if (!open) return;
        setRefs(prevState => images?.map((e, i) => prevState[i] ?? createRef()));
        return () => {
            for (const ref of refs) {
                ref.current = null;
            }
        };
    }, [open, images])

    /**
     * As soon as the container is rendered and visible:
     *
     * - determines the visibility of the buttons.
     * - if there is a startingImage, then scrolls to that image
     */
    useEffect(() => {
        if (!open) return;
        const _interval = () => {
            if (!containerRef.current) {
                return timer = setInterval(_interval, 100);
            }
            clearInterval(timer);
            onScroll()
            const startingIndex = images.findIndex(e => e.first);
            if (startingIndex !== -1) scrollToAnotherImage(false, startingIndex)
        }
        let timer = setInterval(_interval, 100)
        return () => clearInterval(timer);
    }, [open])

    /**
     * Scrolls to the next appropriate image based on the value of next.
     *
     * - resets the current images' zoom scale back to 1
     * - if the forceIndex is available, then scrolls to that index
     * @param {boolean} next whether to scroll to next or prev
     * @param {boolean} forceIndex the index that we are forced to scroll to.
     */
    const scrollToAnotherImage = (next = true, forceIndex) => {
        const current = containerRef.current.scrollLeft / window.innerWidth
        const index = forceIndex ?? Math.floor(current);
        refs.at(index)?.current?.reset();
        if (forceIndex !== undefined) {
            return containerRef.current.scrollTo(window.innerWidth * index, 0);
        }
        if (!next) {
            containerRef.current.scrollTo(window.innerWidth * Math.max(Math.floor(current) - 1, 0), 0);
        } else {
            containerRef.current.scrollTo(window.innerWidth * Math.min(Math.ceil(current) + 1, (images.length ?? 1) - 1), 0);
        }
    }

    /**
     * Determines whether the modal can show the prev or next buttons.
     * - resets the current images' zoom scale back to 1
     */
    const onScroll = () => {
        const current = containerRef.current.scrollLeft / window.innerWidth
        const index = Math.floor(current);
        const prev = Math.max(index - 1, 0);
        const next = Math.min(index + 1, 8);
        refs.at(index)?.current?.reset();
        refs.at(prev)?.current?.reset();
        refs.at(next)?.current?.reset();
        setHasNext(((containerRef.current?.scrollLeft ?? 0) + window.innerWidth) < (containerRef.current?.scrollWidth ?? 0));
        setHasPrev((containerRef.current?.scrollLeft ?? 0) > 0)
    }

    return (
        <>
            <Modal
                open={open}
                onClose={setOpen}
                className={'images-preview-modal'}>
                <Fade in={open} unmountOnExit mountOnEnter>
                    <div>
                        <div className={'images-preview'} onScroll={onScroll} ref={containerRef}>
                            <IconButton
                                className={'close-button'}
                                onPressed={() => setOpen(false)}
                                color={"paper.error"}
                            >
                                <Close className={'icon normal'}/>
                            </IconButton>
                            <Fade in={hasPrev}>
                                <IconButton
                                    className={'prev-button'}
                                    onPressed={() => scrollToAnotherImage(false)}
                                    color={"paper.secondary"}
                                >
                                    <ArrowBackIos className={'icon normal pl-2'}/>
                                </IconButton>
                            </Fade>
                            <div
                                className={'image-container'}
                                onClick={() => setOpen(false)}
                                style={{width: `${(images?.length ?? 1) * 100}vw`}}>
                                {
                                    images?.map((image, i) => (
                                        <Image
                                            image={image}
                                            key={image.src}
                                            imageRef={refs[i]}
                                        />
                                    ))
                                }
                            </div>

                            <Fade in={hasNext}>
                                <IconButton
                                    className={'next-button'}
                                    onPressed={() => scrollToAnotherImage(true)}
                                    color={"paper.secondary"}
                                >
                                    <ArrowForwardIos className={'icon normal px-1'}/>
                                </IconButton>
                            </Fade>
                        </div>
                    </div>
                </Fade>
            </Modal>
        </>
    )
}

const Image = ({image, imageRef}) => {
    const [zoom, setZoom] = useState(1);

    /**
     * Changes the zoom scale of this image.
     * @param {Event} e
     * @param {number} modifier the modifier to specify the direction and how much to change the zoom
     */
    const changeZoom = (e, modifier) => {
        e.stopPropagation();
        e.preventDefault();
        if (modifier > 0) {
            imageRef.current?.zoomIn(modifier);
        } else {
            imageRef.current?.zoomOut(Math.abs(modifier));
        }
    }

    return (
        <div key={image.src} className={'image'}>
            <PrismaZoom
                ref={imageRef}
                onZoomChange={setZoom}
                minZoom={1}
                maxZoom={8}
                onClick={e => e.stopPropagation() || e.preventDefault()}
            >
                <img
                    src={Utils.encodeFileUrl(image.src)}
                    alt={image.alt}
                    onClick={e => e.preventDefault() || e.stopPropagation()}
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    className={'disable-downloading-image'}
                />
            </PrismaZoom>
            <div className={'controller'}>
                <IconButton
                    color={'secondary'}
                    onPressed={(e) => changeZoom(e, -0.1)}
                >
                    <ZoomOut className={'icon normal'}/>
                </IconButton>
                <p>
                    {`${formatMoney(zoom * 100).slice(1, formatMoney(zoom * 100).length - 3)}%`}
                </p>
                <IconButton
                    color={'secondary'}
                    onPressed={(e) => changeZoom(e, 0.1)}
                >
                    <ZoomIn className={'icon normal'}/>
                </IconButton>
            </div>
        </div>
    );
}

export default ImagesPreviewModal;
